import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";
import { Stethoscope, IndianRupee, ChevronLeft, CheckCircle2 } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Patient page that finalises booking with a care provider — only reachable
 * for professionals where the patient has an APPROVED-and-UNUSED referral.
 * No referral → friendly empty state directing the patient to /care-providers.
 */
export default function BookWithProfessional() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [referrals, setReferrals] = useState([]);
  const [pros, setPros] = useState([]);
  const [selected, setSelected] = useState(params.get("pro") || "");
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [serviceId, setServiceId] = useState("anc");
  const [services, setServices] = useState([]);
  const [notes, setNotes] = useState("");
  const [myRecords, setMyRecords] = useState([]);
  const [shareIds, setShareIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: window.location.pathname + window.location.search } });
      return;
    }
    (async () => {
      try {
        const [{ data: refs }, { data: list }, { data: svcs }, { data: recs }] = await Promise.all([
          api.get("/referrals/my"),
          api.get("/professionals"),
          api.get("/services"),
          api.get("/records/my"),
        ]);
        setReferrals(refs);
        setPros(list);
        setServices(svcs);
        setMyRecords(recs);
      } catch (err) {
        toast.error(apiError(err));
      }
    })();
  }, [user, navigate]);

  // Only professionals with an approved-and-unused referral are bookable.
  const approvedPros = useMemo(() => {
    const ids = new Set(referrals.filter((r) => r.status === "approved").map((r) => r.professional_id));
    return pros.filter((p) => ids.has(p.id));
  }, [referrals, pros]);

  // If URL pre-selects a pro but they're not in the approved set, clear it.
  useEffect(() => {
    if (selected && !approvedPros.find((p) => p.id === selected) && approvedPros.length) {
      setSelected(approvedPros[0].id);
    }
  }, [selected, approvedPros]);

  const proObj = useMemo(() => approvedPros.find((p) => p.id === selected), [approvedPros, selected]);

  const refreshSlots = useCallback(async () => {
    if (!selected || !date) { setSlots([]); return; }
    try {
      const { data } = await api.get(`/professionals/${selected}/availability`, { params: { date } });
      setSlots(data.slots || []);
      setSlot("");
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [selected, date]);

  useEffect(() => { refreshSlots(); }, [refreshSlots]);

  const submit = async () => {
    if (!selected || !slot) {
      toast.error("Please pick a time slot.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments/professional", {
        professional_id: selected,
        service_id: serviceId,
        date, slot, notes,
        share_record_ids: shareIds,
      });
      toast.success("Appointment booked. Check your email for confirmation.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleShare = (id) => setShareIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  // No approved referrals → friendly empty state
  if (referrals.length > 0 && approvedPros.length === 0) {
    const pending = referrals.filter((r) => r.status === "pending");
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="book-pro-no-approval">
        <Stethoscope className="w-10 h-10 text-[#5E8B7E] mx-auto mb-4" />
        <h1 className="font-heading text-2xl text-slate-900">Booking is referral-only</h1>
        <p className="text-sm text-slate-500 mt-2">
          To consult one of our care providers, please request a referral first. Once Dr. Prathusha's team
          approves it, you'll be able to book here.
        </p>
        {pending.length > 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3 mt-4">
            You have {pending.length} pending referral request{pending.length > 1 ? "s" : ""} — we'll email you the moment they're approved.
          </p>
        )}
        <Link to="/care-providers">
          <Button data-testid="book-pro-browse-link" className="mt-6 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
            Browse care providers
          </Button>
        </Link>
      </div>
    );
  }

  if (approvedPros.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="book-pro-no-referrals">
        <Stethoscope className="w-10 h-10 text-[#5E8B7E] mx-auto mb-4" />
        <h1 className="font-heading text-2xl text-slate-900">No approved referrals yet</h1>
        <p className="text-sm text-slate-500 mt-2">
          Browse our care providers and request a referral to consult one of them.
        </p>
        <Link to="/care-providers">
          <Button data-testid="book-pro-browse-link" className="mt-6 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
            Browse care providers
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="book-pro-page">
      <Link to="/care-providers" data-testid="book-pro-back" className="text-xs text-slate-500 hover:text-[#2C557E] inline-flex items-center">
        <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back to care providers
      </Link>
      <h1 className="font-heading text-3xl md:text-4xl text-slate-900 mt-1 flex items-center gap-2">
        <CheckCircle2 className="w-7 h-7 text-[#5E8B7E]" /> Book your consultation
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        Your referral has been approved by Dr. Prathusha's team. Pick a slot to finalise your booking.
      </p>

      {approvedPros.length > 1 && (
        <div className="mt-6">
          <Label>Approved professionals</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {approvedPros.map((p) => (
              <button
                key={p.id}
                data-testid={`book-pro-card-${p.id}`}
                onClick={() => setSelected(p.id)}
                className={`text-left bg-white rounded-2xl border p-4 transition-all ${
                  selected === p.id ? "border-[#5E8B7E] ring-2 ring-[#5E8B7E]/30" : "border-[#EDF1EF]"
                }`}
              >
                <p className="font-heading text-lg text-slate-900">{p.name}</p>
                <Badge className="bg-[#D9E4E1] text-[#4A7065] rounded-full text-xs mt-1">{p.specialty}</Badge>
                <p className="text-sm text-slate-700 mt-2 inline-flex items-center gap-1">
                  <IndianRupee className="w-3.5 h-3.5" /> {p.consultation_fee || 0}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {selected && proObj && (
        <div className="bg-white rounded-2xl border border-[#EDF1EF] p-6 mt-6 space-y-5" data-testid="book-pro-form">
          <h2 className="font-heading text-xl text-slate-900">Pick a date & time with {proObj.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Service</Label>
              <select data-testid="book-pro-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)} className="mt-1.5 w-full rounded-xl border border-[#E5EAE8] bg-white px-3 py-2 text-sm">
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Date</Label>
              <DatePicker testId="book-pro-date" value={date} onChange={setDate} className="w-full mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Available slots</Label>
            {slots.length === 0 ? (
              <p data-testid="book-pro-no-slots" className="text-xs text-slate-400 mt-2">No slots available on this date. Try another.</p>
            ) : (
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`book-pro-slot-${s.time}`}
                    disabled={!s.available}
                    onClick={() => setSlot(s.time)}
                    className={`px-3 py-2 rounded-xl text-sm transition-all ${
                      slot === s.time ? "bg-[#5E8B7E] text-white"
                        : s.available ? "bg-[#F4F7F6] hover:bg-[#D9E4E1] text-slate-700"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed line-through"
                    }`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="book-pro-notes">Anything you want them to know (optional)</Label>
            <Textarea id="book-pro-notes" data-testid="book-pro-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1.5 rounded-xl" />
          </div>
          {myRecords.length > 0 && (
            <div className="bg-[#F4F7F6] rounded-xl p-4">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Documents to share with {proObj.name}</Label>
              <p className="text-xs text-slate-500 mt-1">Only the documents you tick will be visible to this professional. Dr. Prathusha always sees everything.</p>
              <ul className="mt-3 space-y-2">
                {myRecords.map((r) => (
                  <li key={r.id} className="flex items-center gap-2.5">
                    <Checkbox
                      id={`share-${r.id}`}
                      data-testid={`book-pro-share-${r.id}`}
                      checked={shareIds.includes(r.id)}
                      onCheckedChange={() => toggleShare(r.id)}
                    />
                    <label htmlFor={`share-${r.id}`} className="text-sm text-slate-700 cursor-pointer">{r.title}</label>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end">
            <Button data-testid="book-pro-submit" onClick={submit} disabled={!slot || loading} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] px-6 py-6">
              {loading ? "Booking…" : `Confirm booking · ₹${proObj.consultation_fee || 0}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
